import { getInstanceId } from '@va/dashboard/selectors/app';
import { get } from '@va/http-client';
import { DynamicPageType, StaticPageType } from '@va/types/website';
import { AutocompleteOptionsFetcher } from '@va/ui/design-system';
import { InfiniteScrollPagination, useFetchData } from '@va/util/hooks';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SWRConfiguration } from 'swr';

const LENGTH = 10;

const defaultQueryFilter = {
  start: 0,
  length: LENGTH,
  search: { general: '' },
};

export const useWebsiteStaticPagesInfiniteScroll = () => {
  const [staticPages, setStaticPages] = useState<StaticPageType[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [queryFilter, setQueryFilter] = useState(defaultQueryFilter);
  const [shouldModifyQueryFilter, setShouldModifyQueryFilter] = useState(true);
  const [controller, setController] = useState(new AbortController());

  const { data: staticPagesData, isValidating } = useGetPagesListSearchable(queryFilter, controller, {
    onSuccess: (data) => {
      if (data?.pageItems?.length === 0) return;
      setShouldModifyQueryFilter(true);
    },
  });

  const observer = useRef<IntersectionObserver | null>(null);

  const lastElementRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (!shouldModifyQueryFilter) return;
        if (!entries[0].isIntersecting) return;
        if (!staticPagesData) return;
        setQueryFilter((prev) => ({ ...prev, start: prev.start + LENGTH }));
        setShouldModifyQueryFilter(false);
      });

      observer.current.observe(node);
    },

    [shouldModifyQueryFilter, staticPagesData],
  );

  useEffect(() => {
    if (!staticPagesData) return;
    setStaticPages((prev) => {
      const newPages: StaticPageType[] = [];
      staticPagesData.pageItems.forEach((page) => {
        const isAlready = prev.find((existentPage) => existentPage.key === page.key);
        if (isAlready) return;
        newPages.push(page);
      });
      return [...prev, ...newPages];
    });
  }, [staticPagesData]);

  const handleChange = useCallback((value: string) => {
    setController((prev) => {
      prev.abort();
      return new AbortController();
    });
    setStaticPages([]);
    setQueryFilter((prev) => ({
      ...prev,
      start: 0,
      search: { general: value },
    }));
  }, []);

  const onSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
      handleChange(e.target.value);
    },
    [handleChange],
  );

  const clearSearchField = useCallback(() => {
    setSearchValue('');
    handleChange('');
  }, [handleChange]);

  return {
    staticPages,
    lastElementRef,
    searchValue,
    clearSearchField,
    onSearch,
    isValidating,
  };
};

export const useAutocompletePagesSuggestions: AutocompleteOptionsFetcher = (
  search: string,
  shouldFetch: boolean,
  pagination: InfiniteScrollPagination,
  controller: AbortController,
) => {
  const mapper = useCallback((data: StaticPagesSearchResponse) => {
    return {
      items: data.pageItems.map(({ key, url }) => ({ value: url, id: key })),
      total: data.PageTotal,
    };
  }, []);

  return useGetPagesListSearchable(
    {
      // This endpoint needs 0-based index pagination
      start: pagination.page - 1,
      length: pagination.pageSize,
      search: { general: search },
    },
    controller,
    {},
    mapper,
    shouldFetch,
  );
};

type PageItem = {
  key: string;
  title: string;
  url: string;
  visits: string;
};

type StaticPagesSearchResponse = { PageTotal: number; pageItems: PageItem[] };

export function useGetPagesListSearchable<T = StaticPagesSearchResponse>(
  queryData: Partial<{
    search: { general: string };
    start: number;
    length: number;
  }> = {},
  controller: AbortController = new AbortController(),
  config?: SWRConfiguration,
  mapper?: (data: StaticPagesSearchResponse) => T,
  shouldFetch = true,
) {
  const websiteId = useSelector(getInstanceId);
  const fetcher = useCallback((url: string, queryData: Record<string, unknown>, controller: AbortController) => {
    return get<StaticPagesSearchResponse>(url, queryData, { signal: controller.signal });
  }, []);

  return useFetchData<T>(
    shouldFetch ? [`/websites/${websiteId}/pages-new`, queryData, controller] : null,
    config,
    mapper,
    fetcher,
  );
}

export const useGetDynamicPages = () => {
  const websiteId = useSelector(getInstanceId);

  return useFetchData<DynamicPageType[]>(`/websites/${websiteId}/dynamic-pages`);
};
